import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: Article | undefined = articles.find(a => a.slug === slug);
  const relatedArticles = articles.filter(a => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">该文章不存在或已被移除</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 福利吧`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://fuliba.com/article/${article.slug}`}
        ogType="article"
        breadcrumbs={[
          { name: '首页', url: 'https://fuliba.com/' },
          { name: article.category, url: 'https://fuliba.com/category/all' },
          { name: article.title, url: `https://fuliba.com/article/${article.slug}` },
        ]}
      />

      <Navbar />

      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <article className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/all" className="hover:text-primary-600 transition-colors cursor-pointer">
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-foreground-500 text-sm">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900
              prose-ul:my-4 prose-li:text-foreground-700 prose-li:mb-2
              prose-a:text-primary-600 hover:prose-a:text-primary-700"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 mt-12 pt-8">
            <h3 className="font-bold font-heading text-foreground-950 text-lg mb-6">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-2 text-foreground-500 text-xs">
                      <span>{related.publishDate}</span>
                      <span>·</span>
                      <span>{related.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishDate,
        image: article.image,
        publisher: {
          '@type': 'Organization',
          name: '福利吧',
        },
      })}} />
    </>
  );
}